import React, { useEffect, useRef, useState } from "react";
import {
  LuCheck,
  LuClock,
  LuCrosshair,
  LuFileMinus2,
  LuFilePen,
  LuFilePlus2,
  LuLoaderCircle,
  LuX,
} from "react-icons/lu";
import * as darvinShared from "../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { CodeContent as CodeContentShared } from "../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
} from "../../../../view_stores/chat_messages/chatModels";
import { useChatMessagesActions } from "../../../../view_stores/chat_messages/useChatMessagesStore";

export function ProjectFilesGenerationMessage({
  message,
}: {
  message: ProjectChatAssistantMessage;
}) {
  const { onAssistantMessageAnimationFinished } = useChatMessagesActions();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isAutoScrollingRef = useRef(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);

  useEffect(() => {
    if (
      message.animationStatus === AssistantMessageAnimationStatus.ANIMATING &&
      message.fullyGenerated
    ) {
      setTimeout(() => {
        onAssistantMessageAnimationFinished();
      }, 400);
    }
  }, [message.animationStatus, message.fullyGenerated]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = (event: Event) => {
      // Ignore scroll events triggered by our auto-scroll
      if (isAutoScrollingRef.current) {
        return;
      }

      // If user scrolls manually, disable auto-scroll
      const target = event.target as HTMLDivElement;
      const isAtBottom =
        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
        1;

      if (!isAtBottom) {
        setIsAutoScrollEnabled(false);
        setShouldShowButton(true);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Only auto-scroll if enabled
    if (!isAutoScrollEnabled) return;

    // Find the processing file element and scroll it into view
    const processingFileElement = scrollContainerRef.current?.querySelector(
      '[data-status="processing"]'
    ) as HTMLElement | null;
    if (processingFileElement && scrollContainerRef.current) {
      isAutoScrollingRef.current = true;
      const container = scrollContainerRef.current;

      // Get the position of the processing element relative to the container
      const containerRect = container.getBoundingClientRect();
      const elementRect = processingFileElement.getBoundingClientRect();
      const relativeTop =
        elementRect.top - containerRect.top + container.scrollTop;

      // Calculate scroll position to show the processing element at the bottom
      // Subtract container height to position element at bottom
      const targetScrollTop =
        relativeTop - containerRect.height + elementRect.height;

      // Smoothly scroll to the target position
      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });

      // Reset the flag after animation completes
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 1000); // Assuming scroll animation takes less than 1s
    }
  }, [
    isAutoScrollEnabled,
    // Create a string that changes when any file status changes
    Object.entries((message.value as CodeContentShared).files)
      .map(([path, file]) => `${path}:${file.status}`)
      .join(","),
  ]);

  // Check if there are any files still being processed
  useEffect(() => {
    const hasActiveFiles = Object.values(
      (message.value as CodeContentShared).files
    ).some(
      (file) =>
        file.status === darvinShared.CodeFileStatusEnum.Enum.processing ||
        file.status === darvinShared.CodeFileStatusEnum.Enum.waiting
    );

    if (!hasActiveFiles) {
      setShouldShowButton(false);
    }
  }, [message.value]);

  if (message.animationStatus === AssistantMessageAnimationStatus.WAITING) {
    return null;
  }

  const hasActiveFiles = Object.values(
    (message.value as CodeContentShared).files
  ).some(
    (file) =>
      file.status === darvinShared.CodeFileStatusEnum.Enum.processing ||
      file.status === darvinShared.CodeFileStatusEnum.Enum.waiting
  );

  const sortedFiles = Object.entries(
    (message.value as CodeContentShared).files
  ).sort(([, a], [, b]) => {
    const statusOrder = {
      [darvinShared.CodeFileStatusEnum.Enum.done]: 0,
      [darvinShared.CodeFileStatusEnum.Enum.error]: 1,
      [darvinShared.CodeFileStatusEnum.Enum.processing]: 2,
      [darvinShared.CodeFileStatusEnum.Enum.waiting]: 3,
    };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  return (
    <div
      className={`${
        message.animationStatus === AssistantMessageAnimationStatus.ANIMATING &&
        "message-container assistant-container"
      } w-full max-h-[320px] flex justify-start`}
    >
      <div
        className={`w-[80%] py-3 px-5 break-words hyphens-auto message-bubble
        rounded-md flex flex-col gap-2 relative
      bg-light-surface-primary dark:bg-[#191C1F] 
      text-light-text dark:text-dark-text 
      border border-white dark:border-[#373B40]`}
      >
        {/* <strong>Processed files</strong> */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex flex-col gap-2 
          overflow-y-auto px-1"
        >
          {sortedFiles.map(([filePath, file]) => (
            <React.Fragment key={filePath}>
              <FileItem
                status={file.status}
                changeType={file.changeType}
                filePath={filePath}
                purpose={file.purpose ?? ""}
              />
            </React.Fragment>
          ))}
        </div>
        {!isAutoScrollEnabled && shouldShowButton && hasActiveFiles && (
          <div className="flex items-center justify-center pr-3">
            <button
              onClick={() => {
                setIsAutoScrollEnabled(true);
                setShouldShowButton(false);
              }}
              className="flex items-center gap-1 text-sm 
              text-primary-blue hover:opacity-50 transition-opacity duration-300"
            >
              <LuCrosshair size={16} />
              <span>Follow updates</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
function FileItem({
  status,
  changeType,
  filePath,
  purpose,
}: {
  status: darvinShared.CodeFileStatus;
  changeType: string;
  filePath: string;
  purpose: string;
}) {
  const getStatusIcon = () => {
    switch (status) {
      case darvinShared.CodeFileStatusEnum.Enum.done:
        return <LuCheck className="text-green-500" size={20} />;
      case darvinShared.CodeFileStatusEnum.Enum.error:
        return <LuX className="text-red-500" size={20} />;
      case darvinShared.CodeFileStatusEnum.Enum.processing:
        return (
          <LuLoaderCircle className="text-blue-500 animate-spin" size={20} />
        );
      case darvinShared.CodeFileStatusEnum.Enum.waiting:
        return <LuClock className="text-yellow-500" size={20} />;
    }
  };

  const getChangeTypeIcon = () => {
    const iconSize = 16;
    switch (changeType) {
      case "new":
        return (
          <LuFilePlus2
            size={iconSize}
            className="text-[#0D6EFD] opacity-[80%]"
            title="new"
          />
        );
      case "modified":
        return (
          <LuFilePen
            size={iconSize}
            className="text-[#D8C640] dark:opacity-[80%]"
            title="modified"
          />
        );
      case "deleted":
        return (
          <LuFileMinus2
            size={iconSize}
            className="text-[#D84041] opacity-[80%]"
            title="deleted"
          />
        );
    }
  };

  return (
    <div
      key={filePath}
      className="flex items-start gap-2 pr-2 mb-2"
      data-status={status}
    >
      <div>{getStatusIcon()}</div>
      <div className="flex flex-col leading-tight gap-1">
        {purpose}
        <div className="flex items-center gap-1">
          {getChangeTypeIcon()}
          <p className="text-sm text-light-placeholder dark:text-dark-placeholder">
            {filePath}
          </p>
        </div>
      </div>
    </div>
  );
}
